import React, { useState, useEffect, useRef } from 'react';

/**
 * A reusable button component for the calculator.
 */
function CalculatorButton({ onClick, children, className = '' }) {
  return (
    <button
      onClick={onClick}
      className={`calculator-key ${className}`}
    >
      {children}
    </button>
  );
}

/**
 * A simple, draggable calculator component.
 */
export default function SimpleCalculator({ onClose }) {
  const [display, setDisplay] = useState('0');
  const [operand, setOperand] = useState(null);
  const [operator, setOperator] = useState(null);
  const [waitingForOperand, setWaitingForOperand] = useState(true);

  // For dragging functionality
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const calculatorRef = useRef(null);

  // Center the calculator on first mount
  useEffect(() => {
    if (calculatorRef.current) {
      const { offsetWidth, offsetHeight } = calculatorRef.current;
      setPosition({
        x: window.innerWidth / 2 - offsetWidth / 2,
        y: window.innerHeight / 2 - offsetHeight / 2,
      });
    }
  }, []);

  // Event handlers for dragging
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add/remove global event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);


  // --- Calculator Logic ---
  const clearDisplay = () => {
    setDisplay('0');
    setOperand(null);
    setOperator(null);
    setWaitingForOperand(true);
  };

  const inputDigit = (digit) => {
    if (waitingForOperand) {
      setDisplay(String(digit));
      setWaitingForOperand(false);
    } else {
      setDisplay(display === '0' ? String(digit) : display + digit);
    }
  };

  const inputDecimal = () => {
    if (waitingForOperand) {
      setDisplay('0.');
      setWaitingForOperand(false);
    } else if (!display.includes('.')) {
      setDisplay(display + '.');
    }
  };

  const toggleSign = () => {
    setDisplay(String(Number((parseFloat(display) * -1).toFixed(3))));
  };

  const inputPercent = () => {
    setDisplay(String(Number((parseFloat(display) / 100).toFixed(3))));  };

  const performOperation = (nextOperator) => {
    const inputValue = parseFloat(display);

    if (operand == null) {
      setOperand(inputValue);
    } else if (operator) {
      const currentValue = operand || 0;
      const newValue = calculate(currentValue, inputValue, operator);
      setOperand(newValue);
      setDisplay(String(Number(newValue.toFixed(3))));
    }

    setWaitingForOperand(true);
    setOperator(nextOperator);
  };

  const calculate = (firstOperand, secondOperand, op) => {
    switch (op) {
      case '+': return firstOperand + secondOperand;
      case '-': return firstOperand - secondOperand;
      case '*': return firstOperand * secondOperand;
      case '/': return firstOperand / secondOperand;
      default: return secondOperand;
    }
  };
  
  return (
    <>
      {/* Inline styles for the calculator */}
      <style>{`
        .calculator {
          position: fixed;
          z-index: 100;
          background-color: #2d3748; /* dark:bg-gray-800 */
          border: 1px solid #4a5568; /* dark:border-gray-600 */
          border-radius: 12px;
          box-shadow: 0 10px 25px rgba(0,0,0,0.3);
          width: 280px;
          user-select: none;
        }
        .calculator-header {
          padding: 8px 12px;
          cursor: move;
          background-color: #4a5568; /* dark:bg-gray-700 */
          color: #e2e8f0; /* dark:text-gray-200 */
          border-top-left-radius: 11px;
          border-top-right-radius: 11px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-weight: bold;
        }
        .calculator-close-btn {
          background: none;
          border: none;
          color: #a0aec0; /* dark:text-gray-400 */
          font-size: 20px;
          line-height: 1;
          cursor: pointer;
        }
        .calculator-close-btn:hover {
          color: #fff;
        }
        .calculator-display {
          background-color: #1a202c; /* dark:bg-gray-900 */
          color: #fff;
          font-size: 2.5rem;
          padding: 16px;
          text-align: right;
          font-family: 'monospace', sans-serif;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .calculator-keypad {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1px;
          background-color: #4a5568; /* dark:border-gray-600 */
          padding: 1px;
          border-bottom-left-radius: 11px;
          border-bottom-right-radius: 11px;
        }
        .calculator-key {
          background-color: #2d3748; /* dark:bg-gray-800 */
          border: none;
          padding: 20px;
          font-size: 1.25rem;
          color: #e2e8f0; /* dark:text-gray-200 */
          cursor: pointer;
          transition: background-color 0.2s;
        }
        .calculator-key:hover {
          background-color: #4a5568; /* dark:bg-gray-700 */
        }
        .key-function {
          background-color: #4a5568; /* dark:bg-gray-700 */
          color: #fff;
        }
        .key-operator {
          background-color: #dd6b20; /* orange-600 */
          color: #fff;
        }
        .key-equals {
          grid-column: span 2;
          background-color: #dd6b20; /* orange-600 */
        }
      `}</style>

      <div
        ref={calculatorRef}
        className="calculator"
        style={{ top: `${position.y}px`, left: `${position.x}px` }}
      >
        <div className="calculator-header" onMouseDown={handleMouseDown}>
          <span>Calculator</span>
          <button className="calculator-close-btn" onClick={onClose}>×</button>
        </div>
        <div className="calculator-display">{display}</div>
        <div className="calculator-keypad">
          <CalculatorButton className="key-function" onClick={clearDisplay}>C</CalculatorButton>
          <CalculatorButton className="key-function" onClick={toggleSign}>+/-</CalculatorButton>
          <CalculatorButton className="key-function" onClick={inputPercent}>%</CalculatorButton>
          <CalculatorButton className="key-operator" onClick={() => performOperation('/')}>÷</CalculatorButton>

          <CalculatorButton onClick={() => inputDigit(7)}>7</CalculatorButton>
          <CalculatorButton onClick={() => inputDigit(8)}>8</CalculatorButton>
          <CalculatorButton onClick={() => inputDigit(9)}>9</CalculatorButton>
          <CalculatorButton className="key-operator" onClick={() => performOperation('*')}>×</CalculatorButton>

          <CalculatorButton onClick={() => inputDigit(4)}>4</CalculatorButton>
          <CalculatorButton onClick={() => inputDigit(5)}>5</CalculatorButton>
          <CalculatorButton onClick={() => inputDigit(6)}>6</CalculatorButton>
          <CalculatorButton className="key-operator" onClick={() => performOperation('-')}>-</CalculatorButton>

          <CalculatorButton onClick={() => inputDigit(1)}>1</CalculatorButton>
          <CalculatorButton onClick={() => inputDigit(2)}>2</CalculatorButton>
          <CalculatorButton onClick={() => inputDigit(3)}>3</CalculatorButton>
          <CalculatorButton className="key-operator" onClick={() => performOperation('+')}>+</CalculatorButton>

          <CalculatorButton style={{gridColumn: 'span 2'}} onClick={() => inputDigit(0)}>0</CalculatorButton>
          <CalculatorButton onClick={inputDecimal}>.</CalculatorButton>
          <CalculatorButton className="key-operator" onClick={() => performOperation('=')}>=</CalculatorButton>
        </div>
      </div>
    </>
  );
}